import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, Typography, Grid, Card, CardContent, Button, Chip, Alert, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`community-tabpanel-${index}`}
      aria-labelledby={`community-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CommunityPortalContent = () => {
  const { user, jwtToken } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [resourceData, setResourceData] = useState({});
  const [partnershipRequests, setPartnershipRequests] = useState([]);
  const [referralNetwork, setReferralNetwork] = useState([]);

  useEffect(() => {
    if (user && jwtToken) {
      loadResourceData();
    }
  }, [user, jwtToken]);

  const loadResourceData = async () => {
    try {
      const response = await fetch(`/api/community-resources/${user.user_id}/dashboard`, {
        headers: { 'Authorization': `Bearer ${jwtToken}` }
      });
      const data = await response.json();
      
      setResourceData(data.overview || {});
      setPartnershipRequests(data.partnerships || []);
      setReferralNetwork(data.network || []);
    } catch (error) {
      console.error('Error loading resource data:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getResourceType = () => {
    if (user?.institution?.toLowerCase().includes('hospital')) return 'Hospital System';
    if (user?.institution?.toLowerCase().includes('clinic')) return 'Clinical Practice';
    if (user?.institution?.toLowerCase().includes('lab')) return 'Diagnostic Laboratory';
    return 'Community Resource';
  };

  // Mock data for demonstration
  const mockResourceData = {
    partners: 23,
    referrals: 156,
    activeTrials: 8,
    utilization: 89
  };

  const mockReferrals = [
    { patientId: 'REF-2024-001', direction: 'Incoming', partner: 'City Hospital', service: 'NCT05432789', status: 'Pending Review', date: '2024-12-10' },
    { patientId: 'REF-2024-002', direction: 'Outgoing', partner: 'Moffitt Cancer Center', service: 'Genetic Testing', status: 'Accepted', date: '2024-12-08' },
    { patientId: 'REF-2024-003', direction: 'Incoming', partner: 'Regional Medical', service: 'Diagnostic Imaging', status: 'Completed', date: '2024-12-05' }
  ];

  const mockTrialOpportunities = [
    {
      nct: 'NCT05432789',
      title: 'Phase II Breast Cancer Immunotherapy Trial',
      sponsor: 'University of Florida',
      needs: ['Diagnostic Imaging', 'Laboratory Services'],
      compensation: '$2,500 per patient',
      timeline: 'Immediate - 18 months',
      match: 95
    },
    {
      nct: 'NCT05987123',
      title: 'Lung Cancer Biomarker Study',
      sponsor: 'Moffitt Cancer Center',
      needs: ['Pathology Review', 'Genetic Testing'],
      compensation: '$1,800 per patient',
      timeline: '6 months - 2 years',
      match: 78
    }
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          aria-label="community portal tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="🏥 Resource Overview" />
          <Tab label="🔬 Clinical Trial Network" />
          <Tab label={`🤝 Patient Referrals (${mockReferrals.length})`} />
          <Tab label="📋 Services Catalog" />
          <Tab label="⚙️ Resource Management" />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Resource Network Overview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your connection to the clinical trial ecosystem and community healthcare network.
          </Typography>

          {/* Key Metrics */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 2, mr: 2 }}>
                    <Typography variant="h4">🏥</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Network Partners</Typography>
                    <Typography variant="h4" fontWeight="bold">{mockResourceData.partners}</Typography>
                    <Typography variant="caption" color="success.main">+3 this quarter</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2, mr: 2 }}>
                    <Typography variant="h4">👥</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Patient Referrals</Typography>
                    <Typography variant="h4" fontWeight="bold">{mockResourceData.referrals}</Typography>
                    <Typography variant="caption" color="success.main">+12 this month</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ p: 2, bgcolor: 'secondary.light', borderRadius: 2, mr: 2 }}>
                    <Typography variant="h4">🔬</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Active Trials</Typography>
                    <Typography variant="h4" fontWeight="bold">{mockResourceData.activeTrials}</Typography>
                    <Typography variant="caption" color="primary.main">2 recruiting</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 2, mr: 2 }}>
                    <Typography variant="h4">📊</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Service Utilization</Typography>
                    <Typography variant="h4" fontWeight="bold">{mockResourceData.utilization}%</Typography>
                    <Typography variant="caption" color="success.main">Above average</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Resource Capabilities & Recent Activity */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} lg={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Our Capabilities</Typography>
                  <Grid container spacing={2}>
                    {[
                      { service: 'Diagnostic Imaging', available: true },
                      { service: 'Laboratory Services', available: true },
                      { service: 'Pathology Review', available: true },
                      { service: 'Genetic Testing', available: false },
                      { service: 'Clinical Research', available: true },
                      { service: 'Patient Support', available: true }
                    ].map((capability, index) => (
                      <Grid item xs={6} key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box 
                            sx={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%', 
                              bgcolor: capability.available ? 'success.main' : 'grey.400' 
                            }} 
                          />
                          <Typography variant="body2" color={capability.available ? 'text.primary' : 'text.secondary'}>
                            {capability.service}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Recent Activity</Typography>
                  <Box sx={{ mt: 2 }}>
                    {[
                      { type: 'referral', message: 'New patient referral from Dr. Smith', time: '2 hours ago', priority: 'high' },
                      { type: 'trial', message: 'Patient enrolled in NCT05432789', time: '1 day ago', priority: 'medium' },
                      { type: 'partnership', message: 'Partnership agreement signed with City Hospital', time: '2 days ago', priority: 'low' },
                      { type: 'service', message: 'Lab results processed for Trial #123', time: '3 days ago', priority: 'medium' }
                    ].map((activity, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                        <Box 
                          sx={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            bgcolor: activity.priority === 'high' ? 'error.main' : 
                                     activity.priority === 'medium' ? 'warning.main' : 'success.main',
                            mt: 1
                          }} 
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">{activity.message}</Typography>
                          <Typography variant="caption" color="text.secondary">{activity.time}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quick Actions */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Quick Actions</Typography>
              <Grid container spacing={2}>
                {[
                  { icon: '📝', title: 'Submit Referral', desc: 'Refer patient to trial', color: 'primary.light' },
                  { icon: '📊', title: 'View Analytics', desc: 'Resource utilization', color: 'success.light' },
                  { icon: '🤝', title: 'Network Directory', desc: 'Find partners', color: 'secondary.light' },
                  { icon: '📋', title: 'Update Services', desc: 'Manage offerings', color: 'warning.light' }
                ].map((action, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <Card variant="outlined" sx={{ cursor: 'pointer', '&:hover': { bgcolor: action.color } }}>
                      <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="h3" sx={{ mb: 1 }}>{action.icon}</Typography>
                        <Typography variant="subtitle2" gutterBottom>{action.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{action.desc}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>
      
      <TabPanel value={activeTab} index={1}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Clinical Trial Network
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Connect with clinical trials that need your resources and expertise.
          </Typography>

          {/* Trial Opportunities */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Available Trial Partnerships</Typography>
                <Button variant="contained" size="small">Express Interest</Button>
              </Box>
              
              {mockTrialOpportunities.map((trial, index) => (
                <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Typography variant="subtitle1" fontWeight="medium">{trial.nct}</Typography>
                          <Chip label={`${trial.match}% Match`} size="small" color="success" />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{trial.title}</Typography>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">Sponsor:</Typography>
                            <Typography variant="body2">{trial.sponsor}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">Timeline:</Typography>
                            <Typography variant="body2">{trial.timeline}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">Services Needed:</Typography>
                            <Typography variant="body2">{trial.needs.join(', ')}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">Compensation:</Typography>
                            <Typography variant="body2">{trial.compensation}</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                        <Button variant="contained" size="small" color="success">Apply</Button>
                        <Button variant="outlined" size="small">Details</Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </CardContent>
          </Card>

          {/* Current Partnerships */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Current Trial Partnerships</Typography>
              {referralNetwork.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>🤝</Typography>
                  <Typography variant="h6" gutterBottom>No Active Partnerships</Typography>
                  <Typography color="text.secondary">
                    Apply to trial partnerships above to start collaborating with research teams.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {referralNetwork.map((partnership, index) => (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="medium">{partnership.trial}</Typography>
                            <Typography variant="body2" color="text.secondary">{partnership.role}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" fontWeight="medium">{partnership.patients} patients</Typography>
                            <Typography variant="caption" color="text.secondary">{partnership.revenue} revenue</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </TabPanel>
      
      <TabPanel value={activeTab} index={2}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Patient Referral Network
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manage incoming and outgoing patient referrals within the clinical trial network.
          </Typography>

          {/* Referral Dashboard */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 2, mr: 2 }}>
                    <Typography variant="h5">📥</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Incoming Referrals</Typography>
                    <Typography variant="h4" fontWeight="bold">24</Typography>
                    <Typography variant="caption" color="primary.main">5 pending review</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2, mr: 2 }}>
                    <Typography variant="h5">📤</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Outgoing Referrals</Typography>
                    <Typography variant="h4" fontWeight="bold">18</Typography>
                    <Typography variant="caption" color="success.main">12 accepted</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ p: 2, bgcolor: 'secondary.light', borderRadius: 2, mr: 2 }}>
                    <Typography variant="h5">✅</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Success Rate</Typography>
                    <Typography variant="h4" fontWeight="bold">87%</Typography>
                    <Typography variant="caption" color="secondary.main">Above network avg</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Referral Management */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Recent Referrals</Typography>
                <Button variant="contained" size="small">Create Referral</Button>
              </Box>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Patient ID</TableCell>
                      <TableCell>Direction</TableCell>
                      <TableCell>Partner</TableCell>
                      <TableCell>Trial/Service</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockReferrals.map((referral, index) => (
                      <TableRow key={index}>
                        <TableCell fontWeight="medium">{referral.patientId}</TableCell>
                        <TableCell>
                          <Chip 
                            label={referral.direction} 
                            size="small"
                            color={referral.direction === 'Incoming' ? 'primary' : 'success'}
                          />
                        </TableCell>
                        <TableCell>{referral.partner}</TableCell>
                        <TableCell>{referral.service}</TableCell>
                        <TableCell>
                          <Chip 
                            label={referral.status} 
                            size="small"
                            color={
                              referral.status === 'Completed' ? 'success' :
                              referral.status === 'Accepted' ? 'primary' : 'warning'
                            }
                          />
                        </TableCell>
                        <TableCell>{referral.date}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button size="small" variant="text">View</Button>
                            {referral.status === 'Pending Review' && (
                              <Button size="small" variant="text" color="success">Accept</Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>
      
      <TabPanel value={activeTab} index={3}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>🚧</Typography>
          <Typography variant="h5" gutterBottom>Services Catalog Section</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            This section is currently under development and will be available soon.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Features will include comprehensive services management tools.
          </Typography>
        </Box>
      </TabPanel>
      
      <TabPanel value={activeTab} index={4}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>🚧</Typography>
          <Typography variant="h5" gutterBottom>Resource Management Section</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            This section is currently under development and will be available soon.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Features will include comprehensive resource management tools.
          </Typography>
        </Box>
      </TabPanel>
    </Box>
  );
};

export default CommunityPortalContent;